import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { AlertTriangle } from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";
import {
  nginxGetSubdomainConfig,
  nginxSaveSubdomainConfig,
} from "../../redux/actionCreator";

/**
 * View + edit the actual `server { }` block(s) for one subdomain — pulled
 * straight from its source file so the user sees the real config, not a
 * regenerated template. Edits are spliced back over just those byte ranges;
 * neighbouring vhosts in the same file are never touched. nginx -t runs
 * before commit, with rollback on failure.
 *
 * Validation on the backend: the saved content must still contain a server
 * block whose server_name includes the original FQDN. To rename, Split then
 * Edit; to remove, use Delete.
 */
export default function EditConfigModal({
  show,
  onHide,
  projectId,
  serverId,
  row,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!show || !row) {
      setContent("");
      setOriginal("");
      setMeta(null);
      setErr(null);
      return;
    }
    const load = async () => {
      setLoading(true);
      setErr(null);
      const res = await dispatch(
        nginxGetSubdomainConfig(projectId, serverId, {
          path: row.path,
          primary_fqdn: row.primary_fqdn,
        })
      );
      setLoading(false);
      if (res?.status) {
        setContent(res.data?.content || "");
        setOriginal(res.data?.content || "");
        setMeta({
          path: res.data?.path,
          block_count: res.data?.block_count,
        });
      } else {
        setErr(res?.message || "Failed to read block");
      }
    };
    load();
  }, [show, row, projectId, serverId, dispatch]);

  const dirty = content !== original;

  const save = async () => {
    setSaving(true);
    setErr(null);
    const res = await dispatch(
      nginxSaveSubdomainConfig(projectId, serverId, {
        path: row.path,
        primary_fqdn: row.primary_fqdn,
        content,
      })
    );
    setSaving(false);
    if (res?.status) {
      setOriginal(content);
      onSaved && onSaved();
      onHide && onHide();
    } else {
      setErr(
        `${res?.message || "Save failed"}` +
          (res?.data?.test_output ? `\n\n${res.data.test_output}` : "")
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => !saving && onHide && onHide()}
      size="xl"
      centered
      backdrop="static"
      fullscreen="lg-down"
    >
      <Modal.Header closeButton={!saving}>
        <div>
          <h5 className="m-0">
            Edit config — <code className="small">{row?.primary_fqdn}</code>
          </h5>
          <div className="text-muted small">
            <code className="small">{meta?.path || row?.path}</code>
            {meta?.block_count > 1 && (
              <> · {meta.block_count} server blocks</>
            )}
            {row?.multi_block && (
              <span className="ms-2 text-warning">
                <AlertTriangle size={12} className="me-1" /> shared file — only this subdomain's blocks are shown; other
                vhosts in the file are untouched
              </span>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <CodeMirror
              value={content}
              height="60vh"
              extensions={[]}
              onChange={(v) => setContent(v)}
              theme="dark"
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
              }}
            />
            {err && (
              <pre
                className="alert alert-danger m-3"
                style={{ whiteSpace: "pre-wrap", fontSize: 12 }}
              >
                {err}
              </pre>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <small className="text-muted">
          Save runs <code>nginx -t</code> first. Source file is rolled back
          from a backup if the test fails. The new content must still contain a
          <code> server_name {row?.primary_fqdn}</code> directive — otherwise
          use Delete.
        </small>
        <div>
          <Button
            variant="light"
            onClick={onHide}
            disabled={saving}
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={saving || loading || !dirty}
          >
            {saving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
